/**
 * Audio Service
 * Handles audio recording, upload, and management for screening questions
 */

import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  getMetadata 
} from 'firebase/storage';
import { db, storage } from '../../../services/firebase/firebase';

/**
 * Upload audio recording to Firebase Storage
 * 
 * @param {Blob} audioBlob - Audio blob data
 * @param {string} userId - User ID
 * @param {string} questionId - Screening question ID
 * @param {number} duration - Recording duration in seconds
 * @returns {Promise<Object>} Upload result with URL and metadata
 */
export const uploadAudioRecording = async (audioBlob, userId, questionId, duration) => {
  try {
    // Validate audio blob
    if (!audioBlob || audioBlob.size === 0) {
      throw new Error('Invalid audio recording');
    }
    
    // Validate duration (max 5 minutes)
    if (duration > 300) {
      throw new Error('Recording too long. Maximum duration is 5 minutes.');
    }
    
    // Create storage reference
    const timestamp = Date.now();
    const fileName = `screening_audio_${userId}_${questionId}_${timestamp}.webm`;
    const storageRef = ref(storage, `audio/screening/${userId}/${fileName}`);
    
    // Upload audio blob
    const uploadResult = await uploadBytes(storageRef, audioBlob, {
      contentType: 'audio/webm'
    });
    
    const downloadURL = await getDownloadURL(uploadResult.ref);
    const metadata = await getMetadata(uploadResult.ref);
    
    // Save audio metadata to Firestore
    const audioData = {
      userId,
      questionId,
      fileName,
      storagePath: uploadResult.ref.fullPath,
      downloadURL,
      fileSize: metadata.size,
      contentType: metadata.contentType,
      duration,
      uploadedAt: serverTimestamp(),
      status: 'active'
    };
    
    const audioRef = doc(db, 'audioResponses', `${userId}_${questionId}_${timestamp}`);
    await setDoc(audioRef, audioData);
    
    return {
      id: audioRef.id,
      ...audioData,
      downloadURL
    };
  } catch (error) {
    console.error('Error uploading audio recording:', error);
    throw error;
  }
};

/**
 * Get user's audio responses for screening questions
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of audio responses
 */
export const getUserAudioResponses = async (userId) => {
  try {
    const audioQuery = query(
      collection(db, 'audioResponses'),
      where('userId', '==', userId),
      where('status', '==', 'active'),
      orderBy('uploadedAt', 'desc')
    );
    
    const snapshot = await getDocs(audioQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user audio responses:', error);
    throw error;
  }
};

/**
 * Get audio response by question ID
 * 
 * @param {string} userId - User ID
 * @param {string} questionId - Question ID
 * @returns {Promise<Object|null>} Audio response or null if not found
 */
export const getAudioResponseByQuestion = async (userId, questionId) => {
  try {
    const audioQuery = query(
      collection(db, 'audioResponses'),
      where('userId', '==', userId),
      where('questionId', '==', questionId),
      where('status', '==', 'active')
    );
    
    const snapshot = await getDocs(audioQuery);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting audio response by question:', error);
    throw error;
  }
};

/**
 * Delete audio recording
 * 
 * @param {string} audioId - Audio document ID
 * @returns {Promise<void>}
 */
export const deleteAudioRecording = async (audioId) => {
  try {
    // Get audio data
    const audioRef = doc(db, 'audioResponses', audioId);
    const audioSnap = await getDoc(audioRef);
    
    if (!audioSnap.exists()) {
      throw new Error('Audio recording not found');
    }
    
    const audioData = audioSnap.data();
    
    // Delete from Firebase Storage
    const storageRef = ref(storage, audioData.storagePath);
    await deleteObject(storageRef);
    
    // Mark as deleted in Firestore (soft delete)
    await updateDoc(audioRef, {
      status: 'deleted',
      deletedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error deleting audio recording:', error);
    throw error;
  }
};

/**
 * Get screening questions
 * 
 * @returns {Promise<Array>} Array of screening questions
 */
export const getScreeningQuestions = async () => {
  try {
    const questionsQuery = query(
      collection(db, 'screeningQuestions'),
      where('status', '==', 'active'),
      orderBy('order', 'asc')
    );
    
    const snapshot = await getDocs(questionsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting screening questions:', error);
    throw error;
  }
};

/**
 * Create or update user's screening profile
 * 
 * @param {string} userId - User ID
 * @param {Array} audioResponses - Array of audio response IDs
 * @returns {Promise<void>}
 */
export const updateScreeningProfile = async (userId, audioResponses) => {
  try {
    const profileData = {
      userId,
      audioResponses,
      completedAt: serverTimestamp(),
      status: 'completed',
      updatedAt: serverTimestamp()
    };
    
    const profileRef = doc(db, 'screeningProfiles', userId);
    await setDoc(profileRef, profileData, { merge: true });
  } catch (error) {
    console.error('Error updating screening profile:', error);
    throw error;
  }
};

/**
 * Get user's screening profile
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Screening profile or null if not found
 */
export const getUserScreeningProfile = async (userId) => {
  try {
    const profileRef = doc(db, 'screeningProfiles', userId);
    const profileSnap = await getDoc(profileRef);
    
    if (profileSnap.exists()) {
      return {
        id: profileSnap.id,
        ...profileSnap.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting screening profile:', error);
    throw error;
  }
};

/**
 * Get audio recording with access control
 * 
 * @param {string} audioId - Audio document ID
 * @param {string} requesterId - ID of user requesting access
 * @returns {Promise<string|null>} Download URL or null if access denied
 */
export const getAudioDownloadURL = async (audioId, requesterId) => {
  try {
    const audioRef = doc(db, 'audioResponses', audioId);
    const audioSnap = await getDoc(audioRef);
    
    if (!audioSnap.exists()) {
      return null;
    }
    
    const audioData = audioSnap.data();
    
    // Check if requester is the owner or has been granted access
    if (audioData.userId === requesterId || audioData.sharedWith?.includes(requesterId)) {
      return audioData.downloadURL;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting audio download URL:', error);
    throw error;
  }
};

/**
 * Share audio responses with employers (for job applications)
 * 
 * @param {Array} audioIds - Array of audio document IDs
 * @param {Array} employerIds - Array of employer/recruiter IDs
 * @returns {Promise<void>}
 */
export const shareAudioWithEmployers = async (audioIds, employerIds) => {
  try {
    const updatePromises = audioIds.map(audioId => {
      const audioRef = doc(db, 'audioResponses', audioId);
      return updateDoc(audioRef, {
        sharedWith: employerIds,
        sharedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });
    
    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error sharing audio with employers:', error);
    throw error;
  }
};

/**
 * Start audio recording (browser-based)
 * 
 * @returns {Promise<Object>} MediaRecorder and stream objects
 */
export const startRecording = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    });
    
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: 'audio/webm;codecs=opus'
    });
    
    const audioChunks = [];
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data);
      }
    };
    
    return {
      mediaRecorder,
      stream,
      audioChunks
    };
  } catch (error) {
    console.error('Error starting recording:', error);
    throw error;
  }
};

/**
 * Stop audio recording and return blob
 * 
 * @param {MediaRecorder} mediaRecorder - MediaRecorder instance
 * @param {MediaStream} stream - MediaStream instance
 * @param {Array} audioChunks - Array of audio chunks
 * @returns {Promise<Blob>} Audio blob
 */
export const stopRecording = (mediaRecorder, stream, audioChunks) => {
  return new Promise((resolve) => {
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      
      // Stop all tracks to release microphone
      stream.getTracks().forEach(track => track.stop());
      
      resolve(audioBlob);
    };
    
    mediaRecorder.stop();
  });
};

export default {
  uploadAudioRecording,
  getUserAudioResponses,
  getAudioResponseByQuestion,
  deleteAudioRecording,
  getScreeningQuestions,
  updateScreeningProfile,
  getUserScreeningProfile,
  getAudioDownloadURL,
  shareAudioWithEmployers,
  startRecording,
  stopRecording
};